import { Flex, Text } from '@mantine/core';
import { ErrorCode, VoucherAllowanceStatus, VoucherStatus } from '@web/common';
import { Card } from '../../../../../component';
import {
  ObtainablePoints,
  ObtainablePointsStatus,
} from '../../../../../component/ObtainablePoints';
import { observer } from 'mobx-react';
import classes from './QuestionnaireItem.module.css';
import { useVoucherStateStore } from '../stores/VoucherStateStore';
import { COLORS } from './constants';
import {
  RedeemButton,
  RedeemedVouchers,
  DonatedStatus,
  ParticipateButton,
  TakenStatus,
} from './VoucherStates';
import { useTranslation } from 'react-i18next';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../../component/notification';
import { MyIcon } from '@web/common-ui';

export interface QuestionnaireItemProps {
  questionnaire: VoucherStatus;
}

export const QuestionnaireItem = observer(
  ({ questionnaire }: QuestionnaireItemProps) => {
    const store = useVoucherStateStore();
    const { t } = useTranslation('questionnaire');

    const {
      surveyIdentifier,
      questionnaireCompleted,
      voucherStatus,
      questionnaireLink,
    } = questionnaire;

    const handleRedeemVoucher = () => {
      if (
        voucherStatus?.type === VoucherAllowanceStatus.AvailableToBeRedeemed
      ) {
        store.redeemVoucher(
          voucherStatus.voucherAllowanceId,
          () => showSuccessNotification(t('RedeemVoucherSuccess')),
          () => showErrorNotification(ErrorCode.CannotRedeemVoucher),
        );
      }
    };

    const renderVoucherContent = () => {
      switch (voucherStatus.type) {
        case VoucherAllowanceStatus.AvailableToBeRedeemed:
          return <RedeemButton onRedeem={handleRedeemVoucher} />;
        case VoucherAllowanceStatus.Redeemed:
          return <RedeemedVouchers vouchers={voucherStatus.vouchers} />;
        case VoucherAllowanceStatus.Donated:
          return <DonatedStatus />;
        case VoucherAllowanceStatus.Taken:
          return <TakenStatus surveyIdentifier={surveyIdentifier} />;
        default:
          return (
            <ParticipateButton
              surveyIdentifier={surveyIdentifier}
              questionnaireLink={questionnaireLink}
            />
          );
      }
    };

    return (
      <Card
        className={classes.card}
        flexProps={{ align: 'start', direction: 'column' }}
      >
        <Flex direction="row" justify="space-between" align="center" w="100%">
          <Flex direction="row" align="center" justify="center" gap="1em">
            <MyIcon name="questionnaire" />
            <Flex direction="column">
              <Text fw={700} size="16px" lh="24px" c={COLORS.TEXT_PRIMARY}>
                {t('Questionnaire')} {surveyIdentifier}
              </Text>
            </Flex>
          </Flex>

          <ObtainablePoints
            points={store.answeredQuestionnairePoints}
            status={
              questionnaireCompleted
                ? ObtainablePointsStatus.Unavailable
                : ObtainablePointsStatus.Available
            }
          />
        </Flex>

        {renderVoucherContent()}
      </Card>
    );
  },
);
